import { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { useVpsStore } from '../../stores/vpsStore';

const TABS = [
  { key: 'from', label: 'From VPS' },
  { key: 'to', label: 'To VPS' },
  { key: 'secondbrain', label: 'SecondBrain' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export function LogViewer() {
  const [activeTab, setActiveTab] = useState<TabKey>('from');
  const logs = useVpsStore((s) => s.logs);
  const fetchLogs = useVpsStore((s) => s.fetchLogs);
  const scrollRef = useRef<HTMLDivElement>(null);

  const loadLogs = useCallback(
    (type: TabKey) => {
      fetchLogs(type, 100);
    },
    [fetchLogs]
  );

  useEffect(() => {
    loadLogs(activeTab);
  }, [activeTab, loadLogs]);

  const currentLines = logs[activeTab] ?? [];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [currentLines]);

  function handleTabChange(tab: TabKey) {
    setActiveTab(tab);
  }

  return (
    <div className="bg-bg-secondary border border-border-subtle rounded-lg overflow-hidden">
      {/* Header with tabs and refresh */}
      <div className="flex items-center justify-between border-b border-border-subtle px-4 py-2">
        <div className="flex gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-t transition-colors ${
                activeTab === tab.key
                  ? 'text-accent-purple border-b-2 border-accent-purple'
                  : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => loadLogs(activeTab)}
          className="flex items-center gap-1.5 text-sm text-text-muted hover:text-text-secondary transition-colors"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Log content */}
      <div
        ref={scrollRef}
        className="max-h-96 overflow-y-auto bg-bg-tertiary p-4 font-mono text-xs leading-relaxed"
      >
        {currentLines.length === 0 ? (
          <p className="text-text-muted">No logs available.</p>
        ) : (
          currentLines.map((line, idx) => (
            <div key={idx} className="flex">
              <span className="inline-block w-8 shrink-0 text-right text-text-muted select-none pr-2">
                {idx + 1}
              </span>
              <span className="text-text-secondary whitespace-pre-wrap break-all">{line}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default LogViewer;
